/**
 * Portion of the native balance safe to wrap without starving the wallet of gas: the balance
 * minus the gas reserve, clamped to never go negative. An unmeasured reserve (`undefined`)
 * yields nothing wrappable, so no caller can turn "no measurement" into "no gas needed".
 */
export const computeWrappableNativeBalance = ({
  nativeBalance,
  nativeGasReserve
}: {
  nativeBalance: bigint
  nativeGasReserve: bigint | undefined
}): bigint =>
  nativeGasReserve !== undefined && nativeBalance > nativeGasReserve
    ? nativeBalance - nativeGasReserve
    : 0n

/**
 * Portion of a gas-token ERC-20 balance safe to spend: the balance minus the gas reserve
 * converted from native units into asset units (the gas token prices gas in its own decimals).
 * Unlike {@link computeWrappableNativeBalance}, an unmeasured reserve (`undefined`) returns the
 * balance unchanged: holding back nothing on a chain whose native gas is the deposited asset
 * would mis-size MAX for every deposit while the gas price is unreadable.
 */
export const computeGasTokenSpendableBalance = ({
  balance,
  nativeGasReserve,
  assetDecimals,
  nativeDecimals = 18
}: {
  balance: bigint
  nativeGasReserve: bigint | undefined
  assetDecimals: number
  nativeDecimals?: number
}): bigint => {
  if (nativeGasReserve === undefined) return balance

  const decimalsDiff = nativeDecimals - assetDecimals
  const assetReserve =
    decimalsDiff >= 0
      ? // Ceil so a sub-unit native reserve still holds back a whole asset unit.
        (nativeGasReserve + 10n ** BigInt(decimalsDiff) - 1n) / 10n ** BigInt(decimalsDiff)
      : nativeGasReserve * 10n ** BigInt(-decimalsDiff)

  return balance > assetReserve ? balance - assetReserve : 0n
}
